import { ref, computed } from 'vue';

/**
 * Display-only column sorting for the matter-context tables. Sorting never
 * mutates the underlying store arrays — `sortedRows` is a sorted copy, so
 * review/publish order is unaffected. Clicking a column cycles
 * asc → desc → unsorted (natural order). Only columns present in
 * `comparators` are sortable.
 */
export type SortDir = 'asc' | 'desc';
export type Comparator<T> = (a: T, b: T) => number;

export function useTableSort<T>(rows: () => T[], comparators: Record<string, Comparator<T>>) {
    const sortKey = ref<string | null>(null);
    const sortDir = ref<SortDir>('asc');

    const toggle = (key: string) => {
        if (!comparators[key]) return;
        if (sortKey.value !== key) {
            sortKey.value = key;
            sortDir.value = 'asc';
        } else if (sortDir.value === 'asc') {
            sortDir.value = 'desc';
        } else {
            // Third click clears the sort, restoring natural order.
            sortKey.value = null;
            sortDir.value = 'asc';
        }
    };

    const sortedRows = computed(() => {
        const list = rows();
        const key = sortKey.value;
        const cmp = key ? comparators[key] : undefined;
        if (!cmp) return list;
        const dir = sortDir.value === 'asc' ? 1 : -1;
        // Array.sort is stable, so equal rows keep their store order.
        return [...list].sort((a, b) => cmp(a, b) * dir);
    });

    /** Current direction for `key`, or null when it isn't the active sort. */
    const sortState = (key: string): SortDir | null =>
        sortKey.value === key ? sortDir.value : null;

    const ariaSort = (key: string): 'ascending' | 'descending' | 'none' => {
        const s = sortState(key);
        return s === 'asc' ? 'ascending' : s === 'desc' ? 'descending' : 'none';
    };

    return { sortKey, sortDir, toggle, sortedRows, sortState, ariaSort };
}
